import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/api';

const GOOGLE_AUTH_URL = process.env.REACT_APP_GOOGLE_AUTH_URL || '';
const AUTH_COOKIE_ONLY = String(process.env.REACT_APP_AUTH_COOKIE_ONLY || '').toLowerCase() === 'true';

// ── Ayudantes de token de sesión ───────────────────────────────────────────
// sessionStorage: persiste solo en la pestaña del navegador (más seguro que localStorage).
// Se usa como respaldo Bearer cuando las cookies quedan bloqueadas por CORS del CDN.
const _KEY = 'elrc_token';
const _saveToken  = (t) => {
  if (AUTH_COOKIE_ONLY) return;
  try { sessionStorage.setItem(_KEY, t); } catch {}
};
const _loadToken  = ()  => {
  if (AUTH_COOKIE_ONLY) return '';
  try { return sessionStorage.getItem(_KEY) || ''; } catch { return ''; }
};
const _clearToken = ()  => {
  if (AUTH_COOKIE_ONLY) return;
  try { sessionStorage.removeItem(_KEY); } catch {}
};

function hasOAuthSessionParam() {
  const hash = window.location.hash || '';
  if (hash.includes('session_id=')) return true;
  return false;
}

function assertApiConfigured(context) {
  if (API) return true;
  console.error(`[Auth] API base URL is empty during ${context}. Check REACT_APP_BACKEND_URL at build time.`);
  return false;
}

// ── Configuración de Axios ─────────────────────────────────────────────────
// Enviar cookies HttpOnly (refresh_token, access_token) en peticiones cross-origin;
// el backend usa CredentialAwareCORSMiddleware (reflejar Origin + Allow-Credentials).
axios.defaults.withCredentials = true;

// Inyectar el token guardado como Authorization: Bearer en cada petición
axios.interceptors.request.use((config) => {
  const token = _loadToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// Interceptor de respuesta para renovar el token automáticamente
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Solo reintentar 401 en endpoints que no son de auth (datos de API, no login/register/refresh/me)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post(`${API}/auth/refresh`);
        if (data?._token) _saveToken(data._token);
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = cargando, false = no autenticado, objeto = autenticado
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  const checkAuth = useCallback(async () => {
    // Si volvemos del callback OAuth, omitir /me.
    // AuthCallback intercambia session_id y establece la sesión primero.
    if (hasOAuthSessionParam()) {
      setLoading(false);
      return;
    }
    if (!assertApiConfigured('checkAuth')) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Renovación periódica del token (cada 20 minutos)
  useEffect(() => {
    if (user && user !== false) {
      refreshIntervalRef.current = setInterval(async () => {
        if (!assertApiConfigured('periodic refresh')) return;
        try {
          const { data } = await axios.post(`${API}/auth/refresh`);
          if (data?._token) _saveToken(data._token);
        } catch (err) {
          console.error('Token refresh failed:', err);
        }
      }, 20 * 60 * 1000); // 20 minutos
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (identifier, password) => {
    if (!assertApiConfigured('login')) {
      throw new Error('API not configured');
    }
    const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
    const { _token, ...user } = data;
    if (_token) _saveToken(_token);
    setUser(user);
    return data;
  }, []);

  const register = useCallback(async (email, password, name, lastName, username) => {
    if (!assertApiConfigured('register')) {
      throw new Error('API not configured');
    }
    const { data } = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      last_name: lastName || null,
      username,
    });
    const { _token, ...user } = data;
    if (_token) _saveToken(_token);
    setUser(user);
    return data;
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    if (!assertApiConfigured('request password reset')) {
      throw new Error('API not configured');
    }
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    if (!assertApiConfigured('reset password')) {
      throw new Error('API not configured');
    }
    const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
    return data;
  }, []);

  const loginWithGoogle = useCallback(() => {
    if (!GOOGLE_AUTH_URL) {
      console.error('REACT_APP_GOOGLE_AUTH_URL is not configured');
      return;
    }
    const redirectUrl = window.location.origin + '/';
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    if (API) {
      try { await axios.post(`${API}/auth/logout`); } catch {}
    }
    _clearToken();
    setUser(false);
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    window.location.href = '/';
  }, []);

  const updateUser = useCallback((userData) => setUser(prev => ({ ...prev, ...userData })), []);

  // Memorizar el valor del contexto para evitar re-renderizados innecesarios
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    requestPasswordReset,
    resetPassword,
    loginWithGoogle,
    logout,
    updateUser,
    checkAuth,
    setUser,
  }), [user, loading, login, register, requestPasswordReset, resetPassword, loginWithGoogle, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
